import React, { useEffect, useState } from "react";
import { fetchApi } from "../utils/fetchApi";
import { useBuilding } from "../context/buildingContext";
import { useRooms } from "../context/RoomsContext";
import TemperatureCard from "../components/TemperatureCard";
import { useTranslation } from "react-i18next";
import DashboardHeader from "../components/DashboardHeader";
import TemperatureUnitSelector from "../components/TemperatureUnitSelector";
import Modal from "../components/Modal";
import { displayFloorAndDoorByName } from '../utils/helpers';
import { Building } from './BuildingSelector';

export interface Room {
  name: string;
  temperature: number | string;
  measuredTemperature: number | string;
  humidity?: number | string;
  heatOrFreeze?: "heat" | "freeze";
  thermostatId: number;
  childLock: boolean;
  childLockCommand?: boolean;
  condensationRiskLevel?: number;
  dewPoint: number;
}

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const { apartmentId, buildingId, apartmentName } = useBuilding();
  const { rooms, setRooms } = useRooms();
  const [connection, setConnection] = useState(true);
  const [outdoorData, setOutdoorData] = useState({ temperature: "N/A", humidity: "N/A" });
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [isQuickSettings, setIsQuickSettings] = useState(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [building, setBuilding] = useState<Building | null>(null);
  const [weatherCode, setWeatherCode] = useState<number | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!apartmentId) {
        console.warn("No apartment ID available");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await fetchApi("get_apartment_data", {
          apartment_id: parseInt(apartmentId),
        });
        setConnection(response.communicationActiveThermostats);
        const thermostats = response.thermostats || [];
        const apartmentMode = response.operationMode;

        // Check if system is disabled at apartment level (use disabledCommand if available, otherwise disabled)
        const isApartmentDisabled = response.disabledCommand ?? response.disabled;

        // Also check building-level disabled
        let isBuildingDisabled = false;
        try {
          const buildingData = await fetchApi("get_building_data", { building_id: parseInt(buildingId!) });
          isBuildingDisabled = buildingData.data.disabled === true;
        } catch (e) {
          console.error("Error fetching building disabled state:", e);
        }

        const isSystemDisabled = isApartmentDisabled || isBuildingDisabled;

        const fetchedRooms = thermostats.map((thermostat: any) => ({
          name: thermostat.name || "",
          temperature: thermostat.temperatureSetpointCommandDegC ? thermostat.temperatureSetpointCommandDegC : thermostat.temperatureSetpointDegC,
          humidity: thermostat.measuredHumPerc ?? "N/A",
          measuredTemperature: thermostat.measuredTempDegC ?? "N/A",
          heatOrFreeze: isSystemDisabled ? undefined : (thermostat.active ? getHeatOrFreeze(apartmentMode) : undefined),
          thermostatId: thermostat.ID,
          condensationRiskLevel: isSystemDisabled ? -1 : thermostat.condensationRiskLevel,
          dewPoint: thermostat.dewPointDegC,
          childLock:
            thermostat.childLockCommand !== null
              ? thermostat.childLockCommand
              : thermostat.childLock ?? false,
        }));
        setRooms(fetchedRooms);
      } catch (error) {
        console.error("Error fetching thermostat data:", error);
        setError(t("dashboard.error.fetch"));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [apartmentId, buildingId, t, setRooms]);

  useEffect(() => {
    const fetchBuilding = async () => {
      if (!buildingId) {
        console.warn("No building ID available");
        return;
      }

      try {
        const response = await fetchApi("get_building_access_rights", {});
        const keys = ['id', 'country', 'postalCode', 'city', 'street', 'temperature', 'humidity', 'name'];

        const buildingData = await fetchApi("get_building_data", {building_id: parseInt(buildingId)});

        const matched = response.find((item: any[]) => item[0] === parseInt(buildingId))
        if (!matched) {
          throw new Error('Building not found');
        }
        const formattedBuilding = keys.reduce((acc, key, index) => {
          acc[key] = matched[index];
          return acc;
        }, {} as Record<string, any>);

        formattedBuilding.disabled = buildingData.data.disabled;
        formattedBuilding.childLock = buildingData.data.childLock;
        formattedBuilding.childLockTemperatureDegC = buildingData.data.childLockTemperatureDegC;
        console.log(formattedBuilding);
        setBuilding(formattedBuilding as Building);
        setWeatherCode(buildingData.data.weatherCode ?? null);

        setOutdoorData({
          temperature: formattedBuilding.temperature !== -1 ? formattedBuilding.temperature?.toString() : "N/A",
          humidity: formattedBuilding.humidity !== -1 ? formattedBuilding.humidity?.toString() : "N/A",
        });
      } catch (error) {
        console.error("Error fetching outdoor data:", error);
      }
    };

    fetchBuilding();
  }, [buildingId]);

  const openModal = (room: Room | null, quickSettings: boolean = false) => {
    setSelectedRoom(room);
    setIsQuickSettings(quickSettings);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedRoom(null);
    setIsQuickSettings(false);
  };

  const getHeatOrFreeze = (apartmentMode: number) => {
    if (apartmentMode === 0) return "heat";
    if (apartmentMode === 1) return "freeze";
    return undefined;
  };

  const handleTemperatureChange = async (newTemperature: number) => {
    try {
      if (isQuickSettings) {
        for (const room of rooms) {
          await fetchApi("update_thermostat_settings", {
            thermostat_id: room.thermostatId,
            temp_setpoint_degC: newTemperature,
          });
        }

        setRooms(
          rooms.map((room) => ({
            ...room,
            temperature: newTemperature,
          }))
        );
      } else if (selectedRoom) {
        const response = await fetchApi("update_thermostat_settings", {
          thermostat_id: selectedRoom.thermostatId,
          temp_setpoint_degC: newTemperature,
        });

        if (response.message === "Thermostat settings updated successfully.") {
          setRooms(
            rooms.map((room) =>
              room.thermostatId === selectedRoom.thermostatId
                ? {
                  ...room,
                  temperature: newTemperature,
                }
                : room
            )
          );
        } else {
          console.error("Unexpected API response:", response);
        }
      }
    } catch (error) {
      console.error("Error updating thermostat settings:", error);
    } finally {
      closeModal();
    }
  };

  return (
    <div className="dashboard">
      {loading ? (
        <p>{t("loading")}</p>
      ) : error ? (
        <p className="error">{error}</p>
      ) : (
        <>
          <DashboardHeader
            building={building}
            outdoorData={outdoorData}
            connection={connection}
            weatherCode={weatherCode}
          />
          <div className="rooms">
            {rooms.map((room) => (
              <TemperatureCard
                key={room.thermostatId}
                room={room.name}
                temperature={room.temperature}
                thermostatId={room.thermostatId}
                measuredTemperature={room.measuredTemperature}
                heatOrFreeze={room.heatOrFreeze}
                humidity={room.humidity}
                dewPoint={room.dewPoint}
                condensationRiskLevel={room.condensationRiskLevel}
                onClick={() => openModal(room)}
                childLock={room.childLock}
              />
            ))}
            <div className="temperatureCard quickSettingsCard" onClick={() => openModal(null, true)}>
              <h3>{t("quickSettings")}</h3>
            </div>
            {isModalOpen && (
              <Modal
                isOpen={isModalOpen}
                onClose={closeModal}
                room={
                  isQuickSettings
                    ? {
                      name: t("setTemperatureAll"),
                      temperature: "24",
                      thermostatId: 0,
                      measuredTemperature: "N/A",
                      childLock: false,
                      dewPoint: 0,
                    }
                    : selectedRoom!
                }
                onTemperatureChange={handleTemperatureChange}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
